import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './SelectFamilyMembersPopup.scss';
import {
  setSelectedMembers,
  setFlightManager,
  setRemovedMembers,
  checkHasFamilySoc,
  setFamilyBundleErrorType,
  setFamilyBundleFailedMembers,
} from '../../CellcomAbroadLobbyBlock/cellcomAbroadBlockSlice';
import { showAppLoader, hideAppLoader } from 'app/Components/Shared/Loader/LoaderSlice';
import { abroadRemoveAndAddSoc } from '../../CellcomAbroadLobbyBlock/Shared/abroadThunk';
import FlightManagerSelector from '../FlightManagerSelector';
import Config from 'app/Config';
import BtnSubmit from 'app/Components/Shared/Buttons/BtnSubmit';

// --- Module-level helpers ----------------------------------------------------

const MAX_ADDITIONAL_MEMBERS = Config.familyBundleMaxAdditionalMembers || 6;

const getPhone = (m) => m?.phone;

const getMemberBan = (member) => member?.ban || member?.productBan;

const enrichFlightManagerFromMembers = (flightManager, members) => {
  if (!flightManager?.phone) return flightManager;
  return members.find(
    m => m.phone === flightManager.phone || m.productSubscriberNo === flightManager.phone
  ) || flightManager;
};

const hasMultipleBansInMembers = (members) => {
  const bans = new Set(members.map(getMemberBan).filter(Boolean));
  return bans.size > 1;
};

const isBusinessUser = (member) => member?.isBusinness === true;

const isDataSubscriber = (member) => {
  if (member?.modemInd === true || member?.modemInd === 'true') return true;
  const features = member?.features || member?.productFeatures || [];
  return features.some(f => f?.featureCode === 'DTONLY' || f?.code === 'DTONLY');
};

// -----------------------------------------------------------------------------

function SelectFamilyMembersPopup(props) {
  const {
    availableMembers = [],
    flightManager = {},
    multipleBans = false,
    editMode = 'edit',
    existingMembers = [],
    onClose,
    onContinue,
    onError,
    onFlightManagerHasFamilySoc,
    socCode,
    blockId,
    startDate,
    endDate,
    countries,
    accountType,
    abroadLobbyBlockId,
  } = props;

  const dispatch = useDispatch();
  const selectedMembersFromRedux = useSelector((state) => state.cellcomAbroad.familyBundleData.selectedMembers);

  const [localSelectedMembers, setLocalSelectedMembers] = useState(selectedMembersFromRedux);
  const [errorMessage, setErrorMessage] = useState('');
  const [localFlightManager, setLocalFlightManager] = useState(flightManager || null);

  // Prevent Redux sync on first mount (local state already seeded from Redux via useState)
  const hasMountedRef = useRef(false);
  const suppressReduxSyncRef = useRef(false);
  // Loader keep-alive interval needed because the global loader is a plain boolean that
  // can be cleared by unrelated components dispatching hideAppLoader mid-request.
  const enforceLoaderIntervalRef = useRef(null);

  const isEditingExistingPackage = !!socCode;
  const canChangeFlightManager = !isEditingExistingPackage;

  const hasMultipleBans = multipleBans || hasMultipleBansInMembers(availableMembers);

  const currentFlightManager = isEditingExistingPackage
    ? flightManager
    : hasMultipleBans ? localFlightManager || flightManager : flightManager;
  const enrichedFlightManager = enrichFlightManagerFromMembers(currentFlightManager, availableMembers);
  const isFlightManagerBusiness = isBusinessUser(enrichedFlightManager);
  const managerBan = getMemberBan(enrichedFlightManager);

  const isFlightManager = (phone) => phone === currentFlightManager?.phone;
  const isMemberSelected = (phone) => isFlightManager(phone) || localSelectedMembers.some(m => getPhone(m) === phone);
  const isExistingMember = (phone) => editMode === 'add' && existingMembers.some(m => getPhone(m) === phone);

  const filteredMembers = availableMembers.filter(member => {
    if (isDataSubscriber(member)) return false;
    if (isFlightManager(member.phone)) return true;
    if (!hasMultipleBans) return true;
    const memberBan = getMemberBan(member);
    return managerBan && memberBan ? memberBan === managerBan : false;
  });

  const sortedMembers = [...filteredMembers].sort((a, b) => {
    if (isFlightManager(a.phone)) return -1;
    if (isFlightManager(b.phone)) return 1;
    return 0;
  });

  const isSingleSubscriber = filteredMembers.length === 1;

  useEffect(() => {
    if (!availableMembers.length || isEditingExistingPackage) return;
    const fm = localFlightManager || flightManager;
    if (!fm?.phone || getMemberBan(fm)) return;
    const enriched = enrichFlightManagerFromMembers(fm, availableMembers);
    if (!getMemberBan(enriched)) return;
    setLocalFlightManager(enriched);
    if (!getMemberBan(flightManager)) {
      dispatch(setFlightManager(enriched));
    }
  }, [availableMembers, flightManager?.phone, isEditingExistingPackage]);

  // Sync from Redux only after initial mount and only when not mid-submit
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (!suppressReduxSyncRef.current) {
      setLocalSelectedMembers(selectedMembersFromRedux);
    }
  }, [selectedMembersFromRedux]);

  // Auto-seed: add flight manager if nothing selected; pre-fill existing members in add mode
  useEffect(() => {
    if (currentFlightManager?.phone && localSelectedMembers.length === 0) {
      const fm = sortedMembers.find(m => m.phone === currentFlightManager.phone);
      if (fm) setLocalSelectedMembers([{ phone: fm.phone, name: fm.name }]);
    }
    if (editMode === 'add' && existingMembers.length > 0 && localSelectedMembers.length === 0) {
      setLocalSelectedMembers(existingMembers);
    }
  }, [currentFlightManager?.phone, editMode, existingMembers]);

  // Remove business users from selection whenever the member list changes
  useEffect(() => {
    const businessPhones = sortedMembers
      .filter(m => isBusinessUser(m) && !isFlightManager(m.phone))
      .map(m => m.phone);
    if (businessPhones.length === 0) return;
    setLocalSelectedMembers(prev => {
      const next = prev.filter(m => !businessPhones.includes(getPhone(m)));
      return next.length === prev.length ? prev : next;
    });
  }, [sortedMembers]);

  // Cleanup loader interval on unmount
  useEffect(() => {
    return () => {
      if (enforceLoaderIntervalRef.current) clearInterval(enforceLoaderIntervalRef.current);
    };
  }, []);

  // --- Handlers ---------------------------------------------------------------

  const handleFlightManagerChange = (member) => {
    if (!canChangeFlightManager) return;
    const newSelection = [{ phone: member.phone, name: member.name }];
    setLocalFlightManager(member);
    dispatch(setFlightManager(member));
    setLocalSelectedMembers(newSelection);
    dispatch(setSelectedMembers(newSelection));
  };

  const handleMemberToggle = (phone) => {
    if (isFlightManager(phone) || isSingleSubscriber || isExistingMember(phone)) return;
    const member = sortedMembers.find(m => m.phone === phone);
    if (member && isBusinessUser(member)) return;

    if (isMemberSelected(phone)) {
      setErrorMessage('');
      setLocalSelectedMembers(prev => prev.filter(m => getPhone(m) !== phone));
    } else {
      const nonManagerCount = localSelectedMembers.filter(m => !isFlightManager(getPhone(m))).length;
      if (nonManagerCount >= MAX_ADDITIONAL_MEMBERS) {
        setErrorMessage(`ניתן לבחור עד ${MAX_ADDITIONAL_MEMBERS + 1} מנויים בחבילה משפחתית`);
        return;
      }
      setErrorMessage('');
      if (member) setLocalSelectedMembers(prev => [...prev, { phone: member.phone, name: member.name }]);
    }
  };

  // Edit-flow: call backend to add/remove members on an existing family bundle
  const submitEditMembers = async (fmPhone, removed, newlyAdded) => {
    const membersToAdd = newlyAdded.filter((member) => member?.phone && member.phone !== fmPhone);
    const membersToRemove = removed
      .filter((phone) => phone && phone !== fmPhone)
      .map((phone) => ({ phone }));

    suppressReduxSyncRef.current = true;
    dispatch(setSelectedMembers(membersToAdd));
    dispatch(setRemovedMembers(membersToRemove));

    dispatch(showAppLoader());
    enforceLoaderIntervalRef.current = setInterval(() => dispatch(showAppLoader()), 150);

    try {
      const result = await dispatch(abroadRemoveAndAddSoc({
        subscriberNumber: fmPhone,
        soc: socCode,
        startDate,
        endDate,
        countries,
        blockId: abroadLobbyBlockId || blockId,
        shouldRemoveSurfingBlockage: false,
        type: accountType ?? null,
        signCollaborationApproval: false,
        isShelfEpi: true,
        isFamilyBundleEdit: true,
      }));

      const body = result?.payload?.Body;

      if (body?.codeError === 'FBM01') {
        const failed = body.FailedMembers || body.failedMembers || [];
        dispatch(setFamilyBundleErrorType('members'));
        dispatch(setFamilyBundleFailedMembers(failed));
        suppressReduxSyncRef.current = false;
        onError?.();
        return;
      }

      if (!body?.isSuccess) {
        suppressReduxSyncRef.current = false;
        onError?.();
        return;
      }

      suppressReduxSyncRef.current = false;
      await Promise.resolve(onContinue?.(localSelectedMembers));
    } finally {
      clearInterval(enforceLoaderIntervalRef.current);
      enforceLoaderIntervalRef.current = null;
      dispatch(hideAppLoader());
    }
  };

  const handleContinue = async () => {
    if(isSingleSubscriber){
      onClose();
      return;
    }
    const nonManagerMembers = localSelectedMembers.filter(m => !isFlightManager(getPhone(m)));

    if (nonManagerMembers.length === 0) {
      setErrorMessage('יש לבחור לפחות מנוי אחד');
      return;
    }
    if (nonManagerMembers.length > MAX_ADDITIONAL_MEMBERS) {
      setErrorMessage(`ניתן לבחור עד ${MAX_ADDITIONAL_MEMBERS + 1} מנויים בחבילה משפחתית`);
      return;
    }

    // Purchase flow: verify flight manager doesn't already have a bundle
    if (!socCode && onFlightManagerHasFamilySoc && currentFlightManager?.phone) {
      dispatch(showAppLoader());
      const result = await dispatch(checkHasFamilySoc({ phoneNumber: currentFlightManager.phone }));
      dispatch(hideAppLoader());
      if (result?.payload?.length > 0) {
        onFlightManagerHasFamilySoc();
        return;
      }
    }

    setErrorMessage('');

    if (socCode) {
      const fmPhone = currentFlightManager?.phone || null;
      const removed = (existingMembers || [])
        .map(getPhone)
        .filter(p => p && p !== fmPhone && !localSelectedMembers.some(m => getPhone(m) === p));
      const existingPhones = new Set((existingMembers || []).map(getPhone));
      const newlyAdded = localSelectedMembers.filter(m => {
        const p = getPhone(m);
        return p !== fmPhone && !existingPhones.has(p);
      });

      if (removed.length === 0 && newlyAdded.length === 0) {
        onContinue?.(localSelectedMembers);
        return;
      }

      await submitEditMembers(fmPhone, removed, newlyAdded);
      return;
    }

    // Purchase flow (no socCode): commit selection to Redux and proceed
    dispatch(setSelectedMembers(localSelectedMembers));
    suppressReduxSyncRef.current = false;
    onContinue?.(localSelectedMembers);
  };

  // --- Render -----------------------------------------------------------------

  return (
    <div className="select-family-members-popup">
      <div className='popup-text'>
        <h2 className="popup-title">
          {isFlightManagerBusiness ? `מנוי ${currentFlightManager?.phone} הינו עסקי` : 'מי יטוס בחבילה?'}
        </h2>
        {sortedMembers.length > MAX_ADDITIONAL_MEMBERS && !isFlightManagerBusiness && (
          <p className="popup-description max-members-notice">יש לבחור עד {MAX_ADDITIONAL_MEMBERS + 1} מנויים</p>
        )}
        {hasMultipleBans && (
          <p className="popup-description">
            המנויים בחבילה צריכים להיות באותו חשבון עם מנהל הטיסה. לבחירת מנויים אחרים יש לבחור מנהל טיסה מחשבון אחר
          </p>
        )}
      </div>

      {hasMultipleBans && (
        <FlightManagerSelector
          flightManager={currentFlightManager}
          availableMembers={availableMembers}
          isOtpMode={false}
          readOnly={isEditingExistingPackage}
          contactNumber={null}
          onFlightManagerChange={handleFlightManagerChange}
          showLogoutButton={false}
          inPopup={true}
        />
      )}

      <div className="members-checkboxes">
        {sortedMembers.map((member) => {
          const isManager = isFlightManager(member.phone);
          const isBusiness = isBusinessUser(member);
          const isExisting = isExistingMember(member.phone);
          const isChecked = isMemberSelected(member.phone) && !isBusiness;
          const isDisabled = isManager || isSingleSubscriber || isBusiness || isExisting || member.hasFamilyBundle;
          const showCheck = isChecked || isExisting || isSingleSubscriber;

          return (
            <div key={member.phone} className="member-checkbox-row">
              <div className="checkbox-wrapper">
                <label className={`checkbox-container ${isDisabled ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={showCheck}
                    disabled={isDisabled}
                    onChange={() => handleMemberToggle(member.phone)}
                  />
                  <span className={`checkmark ${showCheck ? 'checked' : ''} ${isManager || isExisting ? 'checked-disabled' : ''}`}>
                    {showCheck && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke={isManager || isExisting ? '#bdbcbe' : 'var(--theme-primary, #810ea9)'} strokeWidth="2" />
                      </svg>
                    )}
                  </span>
                </label>
              </div>
              <div className={`member-info ${member.hasFamilyBundle ? 'has-family-package' : ''}`}>
                <div className='member'>
                  <span className="member-phone">{member.phone}</span>
                  {member.name && <span className="member-name">{member.name}</span>}
                </div>
                {member.hasFamilyBundle && <span className="family-package-label">המנוי כבר משויך לחבילה משפחתית</span>}
              </div>
            </div>
          );
        })}
        {isFlightManagerBusiness && (
          <div className="business-warning-message">
            <p>לא ניתן להוסיף מנוי עסקי לחבילה משפחתית</p>
          </div>
        )}
      </div>

      {isSingleSubscriber && (
        <div className="single-subscriber-message">
          <p>לא מצאנו מנויים נוספים</p>
          <p>חבילה זו מתאימה למספר אנשים שטסים יחד</p>
          <p>האם להמשיך?</p>
        </div>
      )}

      {errorMessage && <p className="error-message">{errorMessage}</p>}

      <BtnSubmit
        className="continue-btn"
        onclick={handleContinue}
        disabled={isFlightManagerBusiness}
        text={'להתקנת החבילה'}
      />
      {isSingleSubscriber && (
        <BtnSubmit className="secondary-btn" onclick={onClose} text="בחר חבילה אחרת" />
      )}
    </div>
  );
}

export default SelectFamilyMembersPopup;
